import React, { useEffect, useState } from 'react';
import {
  LogoConcept,
  LogoVariant,
  conceptInner,
  getConcept,
  DEFAULT_CONCEPT_ID,
} from '@/lib/logoConcepts';

/* ───────────────────────────────────────────────────────────────────────────
   LIVE PREVIEW OVERRIDE
   ───────────────────────────────────────────────────────────────────────────
   The brand studio (/brand) lets a reviewer see a candidate mark in the real
   header and footer before committing to it. The choice is stored per-browser
   in localStorage and broadcast on a custom event so every mounted mark
   re-renders at once. It is a REVIEW AID only — nothing is shipped to other
   visitors until DEFAULT_CONCEPT_ID in src/lib/logoConcepts.ts is changed.
*/
const STORAGE_KEY = 'giggone.logoConcept';
const EVENT = 'giggone:logoConcept';

export const readLogoConcept = (): string => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) || DEFAULT_CONCEPT_ID;
  } catch {
    return DEFAULT_CONCEPT_ID;
  }
};

export const setLogoConcept = (id: string) => {
  try {
    if (id === DEFAULT_CONCEPT_ID) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* private mode — preview simply does not persist */
  }
  window.dispatchEvent(new CustomEvent(EVENT, { detail: id }));
};

/** Currently previewed concept id, kept in sync across every mounted mark. */
export const useLogoConcept = (): string => {
  const [id, setId] = useState(DEFAULT_CONCEPT_ID);

  useEffect(() => {
    setId(readLogoConcept());
    const sync = () => setId(readLogoConcept());
    window.addEventListener(EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  return id;
};

interface Props {
  /** Pin a specific concept (used by the studio grid). Defaults to the active one. */
  concept?: LogoConcept;
  variant?: LogoVariant;
  /** Rendered pixel size. The art is vector, so any size is crisp. */
  size?: number;
  className?: string;
}

/**
 * The GIGGONE symbol.
 *
 * Artwork comes from the shared registry as markup rather than JSX so that the
 * favicon, the downloadable asset and this component can never disagree. The
 * markup is developer-authored and contains no interpolated user input, which
 * is why dangerouslySetInnerHTML is safe here.
 */
const LogoMark: React.FC<Props> = ({ concept, variant = 'tile', size = 36, className = '' }) => {
  const activeId = useLogoConcept();
  const resolved = concept || getConcept(activeId);

  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      role="img"
      aria-label="GIGGONE"
      className={`shrink-0 ${className}`}
      dangerouslySetInnerHTML={{ __html: conceptInner(resolved, variant) }}
    />
  );
};

export default LogoMark;
